import ReactGA from 'react-ga4';

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';
const isDev = import.meta.env.DEV;

export const initGA = () => {
  if (GA_MEASUREMENT_ID && !isDev) {
    ReactGA.initialize(GA_MEASUREMENT_ID);
  }
};

export const trackPageView = (path: string) => {
  if (isDev) return;
  ReactGA.send({ hitType: 'pageview', page: path });
};

export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number,
) => {
  if (isDev) return;
  ReactGA.event({ category, action, label, value });
};

export const trackNavigation = (destination: string) => {
  trackEvent('Navigation', 'click', destination);
};

export const trackCTAClick = (ctaName: string, location: string) => {
  trackEvent('CTA', 'click', `${ctaName} - ${location}`);
};

export const trackResumeDownload = () => {
  trackEvent('Resume', 'download', 'resume_pdf');
};

export const trackContactFormSubmit = () => {
  trackEvent('Contact', 'form_submit', 'contact_page');
};

export const trackCalendlyClick = (location: string) => {
  // GA category renamed from 'Calendly' to 'Cal.com' after the booking
  // provider switch. Function name kept for compatibility with existing
  // call sites; consider renaming to `trackBookingClick` in a follow-up.
  trackEvent('Cal.com', 'click', location);
};

export const trackSearchQuery = (query: string) => {
  trackEvent('Search', 'query', query);
};

export const trackProjectClick = (projectName: string) => {
  trackEvent('Project', 'view', projectName);
};

export const trackBlogRead = (postTitle: string) => {
  trackEvent('Blog', 'read', postTitle);
};

export const trackExternalLink = (url: string) => {
  trackEvent('External Link', 'click', url);
};

export const trackScrollDepth = (depth: number) => {
  trackEvent('Scroll', 'depth', `${depth}%`, depth);
};

export const trackTimeOnPage = (page: string, seconds: number) => {
  trackEvent('Engagement', 'time_on_page', page, seconds);
};

export const trackSource = () => {
  const params = new URLSearchParams(window.location.search);
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  const campaign = params.get('utm_campaign');

  if (source) {
    trackEvent('Traffic Source', 'visit', `${source}/${medium}/${campaign}`);
  }
};

// --- Exit intent tracking ---

export const trackExitIntent = (trigger: 'mouse_leave' | 'tab_switch' | 'back_button', page: string) => {
  trackEvent('Exit Intent', trigger, page);
};

// --- Content engagement ---

export const trackSectionView = (section: string, page: string) => {
  trackEvent('Content Engagement', 'section_view', `${section} - ${page}`);
};

export const trackExperienceExpand = () => {
  trackEvent('Content Engagement', 'experience_expand', 'show_full_journey');
};

export const trackSkillCategoryView = (category: string) => {
  trackEvent('Content Engagement', 'skills_view', category);
};

export const trackContentCopy = (page: string) => {
  trackEvent('Content Engagement', 'content_copy', page);
};

// --- Recruiter funnel ---

export const trackFunnelStep = (step: string, detail?: string) => {
  trackEvent('Recruiter Funnel', step, detail);
};

// --- Social link clicks (contact page) ---

export const trackSocialClick = (platform: string, page: string) => {
  trackEvent('Social Click', 'click', `${platform} - ${page}`);
};

// --- Home stat cards (hero stats with tooltips) ---

export const trackStatTooltipOpen = (label: string) => {
  trackEvent('Stat Card', 'tooltip_open', label);
};

export const trackStatCtaClick = (label: string, href: string) => {
  trackEvent('Stat Card', 'cta_click', `${label} → ${href}`);
};

// --- Upwork card ---

export const trackUpworkCardView = () => {
  trackEvent('Upwork Card', 'view', 'home');
};

export const trackUpworkCtaClick = (profileUrl: string) => {
  trackEvent('Upwork Card', 'cta_click', profileUrl);
};

// --- Navigation journey ---

const NAV_JOURNEY_KEY = 'nav_journey';

export const recordPageInJourney = (path: string) => {
  try {
    const raw = sessionStorage.getItem(NAV_JOURNEY_KEY);
    const journey: string[] = raw ? JSON.parse(raw) : [];
    journey.push(path);
    sessionStorage.setItem(NAV_JOURNEY_KEY, JSON.stringify(journey));
  } catch { /* ignore */ }
};

export const sendNavigationJourney = () => {
  try {
    const raw = sessionStorage.getItem(NAV_JOURNEY_KEY);
    if (!raw) return;
    const journey: string[] = JSON.parse(raw);
    if (journey.length < 2) return;
    trackEvent('Navigation Journey', 'session_path', journey.join(' → '), journey.length);
    sessionStorage.removeItem(NAV_JOURNEY_KEY);
  } catch { /* ignore */ }
};

// --- Return visitor detection ---

const RETURN_VISITOR_KEY = 'kt_visited';

export const trackReturnVisitor = () => {
  try {
    const visited = localStorage.getItem(RETURN_VISITOR_KEY);
    if (visited) {
      const daysSince = Math.floor((Date.now() - Number(visited)) / 86_400_000);
      trackEvent('Visitor Type', 'returning', `${daysSince}d_since_last`, daysSince);
    } else {
      trackEvent('Visitor Type', 'new', 'first_visit');
    }
    localStorage.setItem(RETURN_VISITOR_KEY, String(Date.now()));
  } catch { /* ignore */ }
};
